import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImagePlus, Palette } from "lucide-react";
import { toast } from "sonner";
import ImagePicker from "@/components/ImagePicker";

const CURRENCIES = [
  { v: "EUR", l: "€ EUR" },
  { v: "USD", l: "$ USD" },
  { v: "GBP", l: "£ GBP" },
  { v: "INR", l: "₹ INR" },
  { v: "JPY", l: "¥ JPY" },
];

const FIELDS = [
  { key: "brand_name", label: "Brand name", placeholder: "Acme Studio" },
  { key: "tagline", label: "Tagline", placeholder: "Designed for the curious" },
  { key: "industry", label: "Industry / Category", placeholder: "Fashion, Beauty, Electronics…" },
  { key: "website", label: "Website", placeholder: "https://acme.co" },
];

const THEME_BG = [
  { key: "dark", label: "Dark · cinematic" },
  { key: "light", label: "Light · editorial" },
  { key: "image", label: "Background image" },
];

const FONTS = [
  { key: "display", label: "Cabinet Grotesk · Display" },
  { key: "helvetica", label: "Helvetica · Neutral" },
  { key: "poppins", label: "Poppins · Modern luxury" },
  { key: "serif", label: "Editorial Serif" },
  { key: "cinzel", label: "Cinzel · Versailles-style" },
  { key: "mono", label: "Plex Mono · Technical" },
];

const ACCENT_PRESETS = ["#8B9D83", "#0F1210", "#B08D57", "#e63946", "#f4a261", "#7c3aed"];

function _LocalImagePicker() { return null; }
// (Using shared <ImagePicker /> from "@/components/ImagePicker" — see import above.)

export default function BrandInfo() {
  const [form, setForm] = useState({
    brand_name: "", tagline: "", industry: "", website: "", description: "", currency: "EUR",
    theme_accent: "#8B9D83", theme_background: "dark", theme_bg_image_path: null,
    theme_logo_path: null, theme_headline: "", theme_cta_label: "", theme_font: "display",
    theme_show_price: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/brand").then((r) => setForm((f) => ({ ...f, ...r.data })));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/brand", form);
      toast.success("Brand details saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
    setBusy(false);
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target?.value !== undefined ? e.target.value : e });
  const setField = (k, v) => setForm({ ...form, [k]: v });

  return (
    <>
      <PageHeader
        eyebrow="Setup"
        title="Brand info & kiosk theme"
        description="Brand basics + customize how the customer experience looks on any device."
      >
        <Button
          onClick={save}
          disabled={busy}
          data-testid="brand-save-button"
          className="rounded-none bg-[#0F1210] hover:bg-black text-white h-11 px-5"
        >
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </PageHeader>

      <div className="p-10 space-y-8 max-w-5xl">
        {/* Brand info */}
        <div className="card-brutalist bg-white border border-[#e5e5e5] p-8">
          <div className="label-overline">Identity</div>
          <h2 className="mt-1 font-display font-bold text-2xl tracking-tight">Brand info</h2>
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="label-overline">{f.label}</Label>
                <Input
                  value={form[f.key] || ""}
                  onChange={update(f.key)}
                  placeholder={f.placeholder}
                  className="mt-2 rounded-none border-[#e5e5e5] focus-visible:ring-[#0F1210] focus-visible:ring-1"
                  data-testid={`brand-${f.key}`}
                />
              </div>
            ))}
            <div>
              <Label className="label-overline">Currency</Label>
              <div className="mt-2 inline-flex border border-[#e5e5e5]">
                {CURRENCIES.map((c, i) => (
                  <button
                    type="button"
                    key={c.v}
                    onClick={() => setField("currency", c.v)}
                    data-testid={`currency-${c.v}`}
                    className={`px-3 py-2 text-sm transition-colors ${
                      form.currency === c.v ? "bg-[#111] text-white" : "bg-white text-[#52525b] hover:bg-[#fafafa]"
                    } ${i > 0 ? "border-l border-[#e5e5e5]" : ""}`}
                  >
                    {c.l}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-[#52525b]">Used for catalog list and the kiosk price reveal.</p>
            </div>
            <div className="md:col-span-2">
              <Label className="label-overline">Description</Label>
              <Textarea
                rows={3}
                value={form.description || ""}
                onChange={update("description")}
                placeholder="Who you are, what you make, who you make it for."
                className="mt-2 rounded-none border-[#e5e5e5] focus-visible:ring-[#0F1210] focus-visible:ring-1"
                data-testid="brand-description"
              />
            </div>
          </div>
        </div>

        {/* Kiosk theme */}
        <div className="card-brutalist bg-white border border-[#e5e5e5] p-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-overline flex items-center gap-2"><Palette className="w-3 h-3" /> Look & feel</div>
              <h2 className="mt-1 font-display font-bold text-2xl tracking-tight">Kiosk theme</h2>
              <p className="mt-1 text-sm text-[#52525b]">
                Customize the customer-facing screen. Changes apply instantly to every kiosk link.
              </p>
            </div>
            <a
              href="#kiosk-preview"
              onClick={(e) => { e.preventDefault(); document.getElementById("kiosk-preview")?.scrollIntoView({ behavior: "smooth" }); }}
              className="label-overline hover:text-[#0F1210]"
            >
              Preview ↓
            </a>
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div>
              <Label className="label-overline">Background style</Label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {THEME_BG.map((b) => (
                  <button
                    key={b.key}
                    onClick={() => setField("theme_background", b.key)}
                    data-testid={`theme-bg-${b.key}`}
                    className={`border p-3 text-xs text-left transition-colors ${
                      form.theme_background === b.key ? "border-[#0F1210] bg-[#0F1210] text-white" : "border-[#e5e5e5] hover:bg-[#fafafa]"
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="label-overline">Headline font</Label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {FONTS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setField("theme_font", f.key)}
                    data-testid={`theme-font-${f.key}`}
                    className={`border p-3 text-xs text-left transition-colors ${
                      form.theme_font === f.key ? "border-[#0F1210] bg-[#0F1210] text-white" : "border-[#e5e5e5] hover:bg-[#fafafa]"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="label-overline">Accent color</Label>
              <div className="mt-2 flex items-center gap-3">
                <input
                  type="color"
                  value={form.theme_accent || "#8B9D83"}
                  onChange={(e) => setField("theme_accent", e.target.value)}
                  className="w-12 h-10 border border-[#e5e5e5] cursor-pointer p-0"
                  data-testid="theme-accent-picker"
                />
                <Input
                  value={form.theme_accent || ""}
                  onChange={update("theme_accent")}
                  placeholder="#8B9D83"
                  className="rounded-none border-[#e5e5e5] font-mono text-xs flex-1"
                  data-testid="theme-accent-hex"
                />
              </div>
              <div className="mt-2 flex gap-1.5">
                {ACCENT_PRESETS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setField("theme_accent", c)}
                    style={{ backgroundColor: c }}
                    className="w-6 h-6 border border-[#e5e5e5] hover:scale-110 transition-transform"
                    title={c}
                  />
                ))}
              </div>
            </div>

            <div>
              <Label className="label-overline">Idle headline</Label>
              <Input
                value={form.theme_headline || ""}
                onChange={update("theme_headline")}
                placeholder="Find your perfect piece."
                className="mt-2 rounded-none border-[#e5e5e5]"
                data-testid="theme-headline"
              />
            </div>

            <div>
              <Label className="label-overline">Reveal CTA label</Label>
              <Input
                value={form.theme_cta_label || ""}
                onChange={update("theme_cta_label")}
                placeholder="I want this"
                className="mt-2 rounded-none border-[#e5e5e5]"
                data-testid="theme-cta"
              />
            </div>

            <div className="flex items-start justify-between gap-4 p-4 border border-[#e5e5e5] bg-[#fafafa]">
              <div>
                <Label className="label-overline">Show product prices on kiosk</Label>
                <p className="mt-1 text-xs text-[#52525b] max-w-md">
                  Turn off if you'd rather keep prices private in-store — customers will see the
                  recommendation without the price tag.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!form.theme_show_price}
                data-testid="theme-show-price-toggle"
                onClick={() => setField("theme_show_price", !form.theme_show_price)}
                className={`relative shrink-0 w-12 h-7 rounded-full transition-colors ${form.theme_show_price ? "bg-[#0F1210]" : "bg-[#d4d4d4]"}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${form.theme_show_price ? "translate-x-5" : ""}`}
                />
              </button>
            </div>

            <ImagePicker
              label="Brand logo (optional)"
              path={form.theme_logo_path}
              onChange={(p) => setField("theme_logo_path", p)}
              testid="theme-logo-input"
            />

            <ImagePicker
              label="Background image (used when background = 'image')"
              path={form.theme_bg_image_path}
              onChange={(p) => setField("theme_bg_image_path", p)}
              testid="theme-bg-input"
            />
          </div>

          {/* Mini preview */}
          <div id="kiosk-preview" className="mt-10">
            <div className="label-overline mb-3">Preview</div>
            <ThemePreview form={form} />
          </div>
        </div>
      </div>
    </>
  );
}

function ThemePreview({ form }) {
  const accent = form.theme_accent || "#8B9D83";
  const isDark = form.theme_background !== "light";
  const bg = form.theme_background === "image" && form.theme_bg_image_path
    ? `url(${API}/files/${form.theme_bg_image_path}) center/cover no-repeat`
    : isDark ? "#0a0a0a" : "#fafafa";
  const fontClass = form.theme_font === "serif" ? "font-serif" : form.theme_font === "mono" ? "font-mono" : form.theme_font === "cinzel" ? "font-cinzel" : form.theme_font === "poppins" ? "font-poppins" : form.theme_font === "helvetica" ? "font-helvetica" : "font-display";
  const fg = isDark ? "#ffffff" : "#111111";
  return (
    <div
      className="relative overflow-hidden border border-[#e5e5e5] h-72 flex flex-col justify-between p-8"
      style={{ background: bg, color: fg }}
      data-testid="theme-preview"
    >
      {form.theme_background === "image" && (
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/70 pointer-events-none" />
      )}
      <div className="relative">
        {form.theme_logo_path ? (
          <img src={`${API}/files/${form.theme_logo_path}`} alt="" className="h-7" />
        ) : (
          <div className="text-xs uppercase tracking-widest opacity-70">{form.brand_name || "Your brand"}</div>
        )}
      </div>
      <div className="relative">
        <h3 className={`${fontClass} font-black tracking-tighter text-3xl leading-[0.95]`} style={{ color: fg }}>
          {form.theme_headline || "Find your perfect piece."}
        </h3>
        <div className="mt-4 inline-flex items-center gap-3">
          <span className="px-4 py-2 text-xs font-medium" style={{ background: accent, color: "#fff" }}>
            {form.theme_cta_label || "I want this"}
          </span>
          <span className="text-xs opacity-70">Tap to begin</span>
        </div>
      </div>
    </div>
  );
}
